import { useReminders, useReminderAction } from "@/hooks/useReminders";
import { format } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { Bell, X, Check, Clock } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

interface Props {
  userTimezone: string;
}

/**
 * The strip that appears when a reminder has fired and still wants an answer.
 *
 * Rendered in normal flow at the top of the content column, NOT fixed to the
 * viewport. It used to be `fixed top-0 left-0 right-0`, which painted it over
 * the app header and across the sidebar — the page title and the search box sat
 * underneath it, unreachable.
 *
 * Actions are labelled rather than abbreviated ("+1h" told you the size of the
 * snooze but not that it was one) and each is confirmed by the row leaving.
 */
export function RemindersBanner({ userTimezone }: Props) {
  const { data } = useReminders({ status: "all" });
  const { snooze, complete } = useReminderAction();
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // "Fired": status='fired', or pending and already due.
  const now = Date.now();
  const fired = (data ?? []).filter((r: any) => {
    if (dismissedIds.has(r.id)) return false;
    if (r.status === "fired") return true;
    return r.status === "pending" && new Date(r.scheduled_for).getTime() <= now;
  });

  if (fired.length === 0) return null;

  const dismiss = (id: string) => setDismissedIds((s) => new Set(s).add(id));

  return (
    <div className="border-b border-primary/25 bg-primary/[0.07]">
      {fired.map((r: any) => {
        const localTime = toZonedTime(new Date(r.scheduled_for), userTimezone);
        return (
          <div
            key={r.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-primary/15 px-4 py-2.5 last:border-b-0 md:px-6"
          >
            <Bell className="h-4 w-4 shrink-0 text-primary-text" />

            <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
              <span className="truncate text-sm font-medium">{r.title}</span>
              <span className="text-xs tabular-nums text-muted-foreground">
                due {format(localTime, "h:mm a")}
              </span>
              {r.note && (
                <span className="hidden truncate text-xs text-muted-foreground lg:inline">
                  · {r.note}
                </span>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-1.5">
              <button
                onClick={() => {
                  complete(r.id);
                  dismiss(r.id);
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-accent px-2.5 py-1.5 text-xs font-medium text-accent-foreground transition-opacity hover:opacity-90"
              >
                <Check className="h-3.5 w-3.5" />
                Mark done
              </button>
              <button
                onClick={() => {
                  snooze(r.id, "1h");
                  dismiss(r.id);
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
              >
                <Clock className="h-3.5 w-3.5" />
                1 hour
              </button>
              <button
                onClick={() => {
                  snooze(r.id, "1d");
                  dismiss(r.id);
                }}
                className="hidden rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-muted sm:inline-flex"
              >
                Tomorrow
              </button>
              <Link
                href="/tasks?view=reminders"
                className="hidden px-1.5 text-xs text-muted-foreground hover:text-foreground md:inline"
              >
                Open
              </Link>
              {/* Hides the strip without changing the reminder — it is still
                  due, and still in the reminders list. */}
              <button
                onClick={() => dismiss(r.id)}
                aria-label="Hide until next reload"
                title="Hide until next reload"
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
